/**
 * DecisionsTile — Phase 12 dashboard tile.
 *
 * Shows decisions generated by the Decision Brain that are awaiting
 * founder action. Each row shows the recommendation, confidence, and
 * the question asked. The tile polls every 10s via `useDecisions`.
 */

import { omegaQueryKeys, useDecisions } from '../hooks/useOmegaState';
import type { PendingDecision } from '../data/types';
import { humanizeDecision } from '../../decisionHumanizer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../../api/http';

const MAX_VISIBLE = 3;

// Compute canonical JSON hash of a flat string-valued object.
// Mirrors gi_signer::canonical::canonical_hash: JCS (sorted keys, no
// whitespace) → SHA-256 → 64-char hex.
async function canonicalHashHex(obj: Record<string, string>): Promise<string> {
  const sorted = Object.fromEntries(Object.keys(obj).sort().map(k => [k, obj[k]]));
  const canonical = JSON.stringify(sorted);
  const bytes = new TextEncoder().encode(canonical);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Sign a request body using the founder warm key via the sign-hash endpoint,
// then POST to the target admin route with X-Founder-Signature header.
async function signedDecisionAction(
  decisionId: string,
  action: 'approve' | 'defer' | 'reject',
): Promise<unknown> {
  const nonce = crypto.randomUUID();
  const body = { decision_id: decisionId, nonce };
  const hashHex = await canonicalHashHex(body);

  // Get signature from the warm-key signing endpoint (requires JWT).
  const signResp = await apiFetch<{ signature: string }>('/admin/founder/sign-hash', {
    method: 'POST',
    body: { hash_hex: hashHex },
  });

  // Submit the decision action with the founder signature.
  return apiFetch(`/admin/decision/${decisionId}/${action}`, {
    method: 'POST',
    body,
    headers: { 'X-Founder-Signature': signResp.signature },
  });
}

export function DecisionsTile() {
  const { data, isPending, isError } = useDecisions();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: omegaQueryKeys.decisions() });

  const approve = useMutation({
    mutationFn: (id: string) => signedDecisionAction(id, 'approve'),
    onSettled: invalidate,
  });

  const defer = useMutation({
    mutationFn: (id: string) => signedDecisionAction(id, 'defer'),
    onSettled: invalidate,
  });

  const allDecisions = data?.decisions ?? [];
  // Filter out seed/synthetic test decisions.
  const decisions = allDecisions.filter(
    (d) => !d.question.toLowerCase().includes('synthetic') &&
            !d.question.toLowerCase().includes('clinical-audit') &&
            !d.question.toLowerCase().includes('clinical audit') &&
            !d.question.toLowerCase().includes('[test]'),
  );
  const overflow = Math.max(0, decisions.length - MAX_VISIBLE);
  const visible = decisions.slice(0, MAX_VISIBLE);

  return (
    <section
      aria-labelledby="decisions-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginTop: 'var(--omega-gap-3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <h2
          id="decisions-heading"
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          Decisions Awaiting You
        </h2>
        {decisions.length > 0 && (
          <span
            style={{
              fontSize: '9pt',
              padding: '1px 7px',
              border: '1px solid var(--omega-state-degraded)',
              color: 'var(--omega-state-degraded)',
              letterSpacing: '0.06em',
              fontWeight: 600,
            }}
          >
            {decisions.length} pending
          </span>
        )}
      </div>

      {/* Body */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt' }}>
          Loading…
        </div>
      )}

      {isError && (
        <div style={{ color: 'var(--omega-state-refused)', fontSize: '10pt' }}>
          Failed to load decisions.
        </div>
      )}

      {!isPending && !isError && visible.length === 0 && (
        <div
          style={{
            color: 'var(--omega-ink-subtle)',
            fontSize: '10pt',
            letterSpacing: '0.04em',
            fontStyle: 'italic',
          }}
        >
          No decisions pending — all clear.
        </div>
      )}

      {visible.map((d) => (
        <DecisionRow
          key={d.decision_id}
          decision={d}
          onApprove={() => approve.mutate(d.decision_id)}
          onDefer={() => defer.mutate(d.decision_id)}
          busy={approve.isPending || defer.isPending}
        />
      ))}

      {overflow > 0 && (
        <div
          style={{
            marginTop: 'var(--omega-gap-1)',
            fontSize: '9pt',
            color: 'var(--omega-ink-subtle)',
            letterSpacing: '0.06em',
          }}
        >
          + {overflow} more pending
        </div>
      )}
    </section>
  );
}

function DecisionRow({
  decision: d,
  onApprove,
  onDefer,
  busy,
}: {
  decision: PendingDecision;
  onApprove: () => void;
  onDefer: () => void;
  busy: boolean;
}) {
  const recColor =
    d.recommendation === 'approve'
      ? 'var(--omega-state-healthy)'
      : d.recommendation === 'defer'
        ? 'var(--omega-state-degraded)'
        : 'var(--omega-state-refused)';

  const confColor =
    d.confidence === 'high'
      ? 'var(--omega-state-refused)'
      : d.confidence === 'medium'
        ? 'var(--omega-state-degraded)'
        : 'var(--omega-ink-subtle)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--omega-gap-2)',
        padding: 'var(--omega-gap-1) 0',
        borderTop: '1px solid var(--omega-rim)',
      }}
    >
      {/* Recommendation badge */}
      <span
        style={{
          flexShrink: 0,
          fontSize: '8pt',
          padding: '1px 5px',
          border: `1px solid ${recColor}`,
          color: recColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {d.recommendation}
      </span>

      {/* Confidence badge */}
      <span
        style={{
          flexShrink: 0,
          fontSize: '8pt',
          padding: '1px 5px',
          border: `1px solid ${confColor}`,
          color: confColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {d.confidence}
      </span>

      {/* Human-readable decision (WHAT + WHY) */}
      <div className="ltr-content" style={{ flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            fontSize: '10pt',
            color: 'var(--omega-ink)',
          }}
        >
          {humanizeDecision(d)}
        </div>
      </div>

      {/* Actions */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 6 }}>
        <ActionBtn label="Approve" onClick={onApprove} disabled={busy} accent />
        <ActionBtn label="Defer" onClick={onDefer} disabled={busy} />
      </div>
    </div>
  );
}

function ActionBtn({
  label,
  onClick,
  disabled,
  accent,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: '8pt',
        padding: '2px 8px',
        border: `1px solid ${accent ? 'var(--omega-state-healthy)' : 'var(--omega-rim-strong)'}`,
        background: 'transparent',
        color: accent
          ? 'var(--omega-state-healthy)'
          : 'var(--omega-ink-muted)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}
